'use client'

// Checkpoint Service - automaatne andmete salvestamine
interface CheckpointData {
  timestamp: string
  user?: any
  companies: any[]
  invoices: any[]
  customers: any[]
  activeCompany?: any
  session?: any
  version: string
}

export class CheckpointService {
  private static readonly CHECKPOINT_KEY = 'app_checkpoint'
  private static readonly VERSION = '1.0.0'

  // Loo checkpoint
  static createCheckpoint(data: Partial<CheckpointData>): CheckpointData {
    const checkpoint: CheckpointData = {
      timestamp: new Date().toISOString(),
      version: this.VERSION,
      companies: data.companies || [],
      invoices: data.invoices || [],
      customers: data.customers || [],
      activeCompany: data.activeCompany,
      user: data.user,
      session: data.session
    }
    
    this.saveCheckpoint(checkpoint)
    console.log('✅ Checkpoint created:', checkpoint.timestamp)
    return checkpoint
  }

  // Salvesta checkpoint localStorage-sse
  static saveCheckpoint(checkpoint: CheckpointData): void {
    try {
      localStorage.setItem(this.CHECKPOINT_KEY, JSON.stringify(checkpoint))
    } catch (error) {
      console.error('Failed to save checkpoint:', error)
    }
  }

  // Lae checkpoint
  static loadCheckpoint(): CheckpointData | null {
    try {
      const saved = localStorage.getItem(this.CHECKPOINT_KEY)
      return saved ? JSON.parse(saved) : null
    } catch (error) {
      console.error('Failed to load checkpoint:', error)
      return null
    }
  }

  // Võta kasutaja andmed checkpoint-ist
  static getUserData(): any | null {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.user || null
  }

  // Võta ettevõtete andmed checkpoint-ist
  static getCompaniesData(): any[] {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.companies || []
  }

  // Võta arvete andmed checkpoint-ist
  static getInvoicesData(): any[] {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.invoices || []
  }

  // Võta klientide andmed checkpoint-ist
  static getCustomersData(): any[] {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.customers || []
  }

  // Võta aktiivne ettevõte checkpoint-ist
  static getActiveCompany(): any | null {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.activeCompany || null
  }

  // Võta sessiooni andmed checkpoint-ist
  static getSessionData(): any | null {
    const checkpoint = this.loadCheckpoint()
    return checkpoint?.session || null
  }

  // Uuenda kasutaja andmed checkpoint-ist
  static updateUserData(userData: any): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.user = userData
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Uuenda ettevõtete andmed checkpoint-ist
  static updateCompaniesData(companies: any[]): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.companies = companies
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Uuenda arvete andmed checkpoint-ist
  static updateInvoicesData(invoices: any[]): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.invoices = invoices
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Uuenda klientide andmed checkpoint-ist
  static updateCustomersData(customers: any[]): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.customers = customers
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Uuenda aktiivne ettevõte checkpoint-ist
  static updateActiveCompany(company: any): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.activeCompany = company
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Uuenda sessiooni andmed checkpoint-ist
  static updateSessionData(session: any): void {
    const checkpoint = this.loadCheckpoint() || this.createCheckpoint({})
    checkpoint.session = session
    checkpoint.timestamp = new Date().toISOString()
    this.saveCheckpoint(checkpoint)
  }

  // Eemalda checkpoint
  static clearCheckpoint(): void {
    localStorage.removeItem(this.CHECKPOINT_KEY)
    console.log('🗑️ Checkpoint cleared')
  }

  // Checkpoint statistika
  static getCheckpointInfo(): { timestamp: string | null, size: number, version: string } {
    const checkpoint = this.loadCheckpoint()
    const size = checkpoint ? JSON.stringify(checkpoint).length : 0
    
    return {
      timestamp: checkpoint?.timestamp || null,
      size: size,
      version: checkpoint?.version || 'N/A'
    }
  }

  // Automaatne checkpoint iga 5 minuti järel (kasutab window.setInterval)
  static startAutoCheckpoint(intervalMinutes: number = 5): void {
    if (typeof window === 'undefined') return
    
    const intervalMs = intervalMinutes * 60 * 1000
    setInterval(() => {
      this.createCheckpoint({
        user: this.getUserData(),
        companies: this.getCompaniesData(),
        invoices: this.getInvoicesData(),
        customers: this.getCustomersData(),
        activeCompany: this.getActiveCompany(),
        session: this.getSessionData()
      })
      console.log('🔄 Auto checkpoint created')
    }, intervalMs)
    
    console.log(`🚀 Auto checkpoint started (every ${intervalMinutes} minutes)`)
  }
}

// React hook checkpoint andmete jaoks
export function useCheckpoint() {
  return {
    // Getterid
    getUserData: CheckpointService.getUserData.bind(CheckpointService),
    getCompaniesData: CheckpointService.getCompaniesData.bind(CheckpointService),
    getInvoicesData: CheckpointService.getInvoicesData.bind(CheckpointService),
    getCustomersData: CheckpointService.getCustomersData.bind(CheckpointService),
    getActiveCompany: CheckpointService.getActiveCompany.bind(CheckpointService),
    getSessionData: CheckpointService.getSessionData.bind(CheckpointService),
    getCheckpointInfo: CheckpointService.getCheckpointInfo.bind(CheckpointService),
    
    // Setterid
    updateUserData: CheckpointService.updateUserData.bind(CheckpointService),
    updateCompaniesData: CheckpointService.updateCompaniesData.bind(CheckpointService),
    updateInvoicesData: CheckpointService.updateInvoicesData.bind(CheckpointService),
    updateCustomersData: CheckpointService.updateCustomersData.bind(CheckpointService),
    updateActiveCompany: CheckpointService.updateActiveCompany.bind(CheckpointService),
    updateSessionData: CheckpointService.updateSessionData.bind(CheckpointService),
    
    // Utility
    createCheckpoint: CheckpointService.createCheckpoint.bind(CheckpointService),
    clearCheckpoint: CheckpointService.clearCheckpoint.bind(CheckpointService),
    startAutoCheckpoint: CheckpointService.startAutoCheckpoint.bind(CheckpointService)
  }
}